import { ChainId } from "@pancakeswap/chains"
import { TickMath } from "@pancakeswap/v3-sdk"
import axios from "axios"
import { MORALIS_API, MORALIS_API_KEY } from "config/constants/endpoints"
import { gql, GraphQLClient } from "graphql-request"
import useSWRImmutable from "swr/immutable"
import { v3Clients } from "utils/graphql"

interface PriceData {
    token0Price: number
    token1Price: number
    liquidity: number
    volume: number
}

export function use9mmPrices(token0Address: string, token1Address: string) {
    const client = new GraphQLClient('https://info-api.9mm.pro/subgraphs/name/pulsechain/9mm-v3')
    return useSWRImmutable(
        ['info/pool/9mm/prices', token0Address, token1Address],
        async () => {
            const { pools } = await client.request<{ pools: any[] }>(gql`
                query($token0: String!, $token1: String!) {
                    pools(where: { token0: $token0, token1: $token1, totalValueLockedUSD_gt: 0 }) {
                        token0Price token1Price liquidity:totalValueLockedUSD volumeUSD tick
                    }
                }
            `, {
                token0: token0Address.toLowerCase(), token1: token1Address.toLowerCase()
            })
            return pools.filter(pool => Number(pool.tick) > TickMath.MIN_TICK * 0.9 && Number(pool.tick) < TickMath.MAX_TICK * 0.9).map(pool => ({
                token0Price: Number(pool.token0Price),
                token1Price: Number(pool.token1Price),
                liquidity: Number(pool.liquidity),
                volume: Number(pool.volumeUSD),
            } as PriceData)).sort((a, b) => b.liquidity - a.liquidity).slice(0, 1)
        },
        {
            // keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
}

export function usePulseXPrices(token0Address: string, token1Address: string) {
    const client = new GraphQLClient('https://graph.pulsechain.com/subgraphs/name/pulsechain/pulsexv2')
    return useSWRImmutable(
        ['info/pool/pulsex/prices', token0Address, token1Address],
        async () => {
            const { pairs } = await client.request<{ pairs: any[] }>(gql`
                query($token0: String!, $token1: String!) {
                    pairs(where: { token0: $token0, token1: $token1, reserveUSD_gt: 0 }) {
                        token0Price token1Price liquidity:reserveUSD volumeUSD
                    }
                }
            `, {
                token0: token0Address.toLowerCase(), token1: token1Address.toLowerCase()
            })
            return pairs.map(pair => ({
                token0Price: Number(pair.token0Price),
                token1Price: Number(pair.token1Price),
                liquidity: Number(pair.liquidity),
                volume: Number(pair.volumeUSD),
            } as PriceData)).sort((a, b) => b.liquidity - a.liquidity).slice(0, 1)
        },
        {
            // keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
}

export function use9inchPrices(token0Address: string, token1Address: string) {
    const client = new GraphQLClient('https://subgraphs.pdex.vision/subgraphs/name/pulsechain/v2')
    return useSWRImmutable(
        ['info/pool/9inch/prices', token0Address, token1Address],
        async () => {
            const { pairs } = await client.request<{ pairs: any[] }>(gql`
                query($token0: String!, $token1: String!) {
                    pairs(where: { token0: $token0, token1: $token1, reserveUSD_gt: 0 }) {
                        token0Price token1Price liquidity:reserveUSD volumeUSD
                    }
                }
            `, {
                token0: token0Address.toLowerCase(), token1: token1Address.toLowerCase()
            })
            return pairs.map(pair => ({
                token0Price: Number(pair.token0Price),
                token1Price: Number(pair.token1Price),
                liquidity: Number(pair.liquidity),
                volume: Number(pair.volumeUSD),
            } as PriceData)).sort((a, b) => b.liquidity - a.liquidity).slice(0, 1)
        },
        {
            // keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
}

export function useMoralisPrices(token0Address: string, token1Address: string) {
    return useSWRImmutable(
        ['info/pool/moralis/prices', token0Address, token1Address],
        async () => {
            const { data: prices } = await axios.post(`${MORALIS_API}/erc20/prices?chain=pulse`,
                {
                    tokens: [{ token_address: token0Address }, { token_address: token1Address }]
                },
                {
                    headers: {
                        'accept': 'application/json',
                        'X-API-Key': MORALIS_API_KEY
                    }
                }
            ).catch(() => ({ data: [] }))
            return {
                token0Price: Number(prices[1].usdPrice) / Number(prices[0].usdPrice),
                token1Price: Number(prices[0].usdPrice) / Number(prices[1].usdPrice),
            }
        },
        {
            // keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
}

export function useNativePrice() {
    return useSWRImmutable(
        'info/price/native',
        async () => {
            const { bundle } = await v3Clients[ChainId.PULSE].request<{ bundle: any }>(gql`
                query {
                    bundle(id: "1") {
                        price: ethPriceUSD
                    }
                }
            `)
            return Number(bundle.price)
        },
        {
            // keepPreviousData: true,
            refreshInterval: 60_000
        },
    )
}